import React, { useState, useRef, useEffect, useCallback, ReactNode, ChangeEvent, KeyboardEvent } from "react";
import Icon from "@mdi/react";
import { mdiPaperclip, mdiArrowUp } from "@mdi/js";
import { Box, Paper, IconButton, InputBase, CircularProgress } from "@mui/material";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { MessageProps, Message } from "./Message";
import { ConfirmationMessage, ConfirmationMessageProps } from "./ConfirmationMessage";
import { useSizeEffect } from "shared/hooks";

// Tipagem das Props do Chat
interface ChatProps {
	onSend: (message: string, files?: FileList | null) => Promise<void> | void;
	fileTypes?: string[];
	children?: ReactNode;
	className?: string;
	placeholder?: string;
	disabled?: boolean;
}

const Chat: React.FC<ChatProps> & { Message: React.FC<MessageProps>; Confirmation: React.FC<ConfirmationMessageProps> } = ({
	onSend,
	fileTypes = [],
	children,
	className,
	placeholder = "Envie uma mensagem...",
	disabled = false,
}) => {
	const [loading, setLoading] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const lastIdMessageRef = useRef<string | null>(null);
	const inputSpacerRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	// Auto-scroll inteligente: acompanha novas mensagens, mas pausa
	// se o usuário fizer scroll manual para cima.
	const isUserScrolledUpRef = useRef(false);

	const handleWrapperScroll = useCallback(() => {
		const el = wrapperRef.current;
		if (!el) return;
		// column-reverse: scrollTop === 0 = fundo; scrollTop < 0 = usuário subiu
		// Em browsers que usam valor positivo, comparamos com a distância restante
		const threshold = 80;
		const atBottom = Math.abs(el.scrollTop) <= threshold;
		isUserScrolledUpRef.current = !atBottom;
	}, []);

	const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
		messagesEndRef.current?.scrollIntoView({ behavior });
		isUserScrolledUpRef.current = false;
	}, []);

	// Scroll ao surgir nova mensagem (novo ID), exceto se usuário subiu
	useEffect(() => {
		const ids = React.Children.map(children, (child) => {
			if (React.isValidElement(child) && "props" in child && (child.props as any).id) {
				return (child.props as any).id;
			}
			return null;
		});

		const lastId = ids ? ids[ids.length - 1] : null;

		if (lastId !== lastIdMessageRef.current) {
			if (!isUserScrolledUpRef.current) {
				scrollToBottom();
			}
		}

		lastIdMessageRef.current = lastId;
	}, [children, scrollToBottom]);

	// Observer: auto-scroll durante streaming enquanto usuário está no fundo
	useEffect(() => {
		const sentinel = messagesEndRef.current;
		const container = wrapperRef.current;
		if (!sentinel || !container) return;

		const observer = new MutationObserver(() => {
			if (!isUserScrolledUpRef.current) {
				sentinel.scrollIntoView({ behavior: "auto" });
			}
		});

		observer.observe(container, { childList: true, subtree: true, characterData: true });
		return () => observer.disconnect();
	}, []);

	const handleSend = async () => {
		if (inputValue.trim()) {
			setLoading(true);
			// Ao enviar, sempre volta ao fundo
			scrollToBottom();
			await Promise.all([onSend(inputValue, fileInputRef.current?.files), new Promise((resolve) => setTimeout(resolve, 1000))]);
			setInputValue("");
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			setLoading(false);
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleFileClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		// Optional: Handle file preview or immediate send logic here if needed
	};

	const inputAreaRef = useSizeEffect<HTMLDivElement>((size) => {
		if (inputSpacerRef.current) {
			inputSpacerRef.current.style.height = `${size.height}px`;
		}
	});

	return (
		<Box className={clsx(styles.chatContainer, className)}>
			<div
				className={styles.messagesWrapper}
				ref={wrapperRef}
				onScroll={handleWrapperScroll}
			>
				<Box className={styles.messagesList}>
					{children}
					<div
						className={styles.inputSpacer}
						ref={inputSpacerRef}
					/>
					<div ref={messagesEndRef} />
				</Box>
			</div>

			<Box
				className={styles.inputArea}
				ref={inputAreaRef}
			>
				<Paper
					className={styles.inputContainer}
					elevation={0}
				>
					<IconButton
						className={styles.actionButton}
						onClick={handleFileClick}
						title="Anexar arquivo"
						size="small"
						disabled
					>
						<Icon
							path={mdiPaperclip}
							size={1}
						/>
					</IconButton>

					<input
						type="file"
						ref={fileInputRef}
						className={styles.fileInput}
						accept={fileTypes.join(",")}
						onChange={handleFileChange}
						multiple
					/>

					<div style={{ minHeight: "34px", flex: 1, display: "flex", alignItems: "center" }}>
						<InputBase
							name="messageInput"
							className={styles.textArea}
							placeholder={placeholder}
							multiline
							maxRows={6}
							value={inputValue}
							onChange={(e) => setInputValue(e.target.value)}
							onKeyDown={handleKeyDown}
							disabled={loading || disabled}
						/>
					</div>

					<IconButton
						className={clsx(styles.actionButton, styles.sendButton)}
						onClick={handleSend}
						disabled={inputValue.trim() === "" || loading || disabled}
						title="Enviar"
						size="small"
					>
						{loading ? (
							<CircularProgress size={20} />
						) : (
							<Icon
								path={mdiArrowUp}
								size={1}
							/>
						)}
					</IconButton>
				</Paper>
				{/* <Typography
					className={styles.disclaimer}
					variant="caption"
				>
					O Guia Med.IA pode cometer erros. Verifique informações importantes.
				</Typography> */}
			</Box>
		</Box>
	);
};

// Assign subcomponents
Chat.Message = Message;
Chat.Confirmation = ConfirmationMessage;

export { Chat };
